import React, { useState } from "react";
import {
  Home,
  Building,
  Droplets,
  Sofa,
  Car,
  Wind,
  Calendar,
  Sparkles,
  CheckCircle,
  Shield,
  Clock,
  Users,
  ChevronRight,
  Star,
  Award,
  ThumbsUp,
  Bed,
  Square,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState("residential");

  // WhatsApp Booking Handler
  const handleWhatsAppBooking = (service) => {
    const phoneNumber = "61420499661";
    
    const message = `*BOOKING REQUEST - ${service.title}* 📋

🏢 *Service Details:*
• Service: ${service.title}
• Category: ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Cleaning
• Price: ${service.price}
• Duration: ${service.duration}

📝 *Description:*
${service.description}

✨ *Service Features:*
${service.features.map(feature => `• ${feature}`).join('\n')}

${service.popular ? '🔥 *POPULAR SERVICE*' : ''}

👤 *My Details (Please fill in):*
• Full Name: [Your Name]
• Address: [Full Address]
• Property Type: [House/Apartment/Office]
• Preferred Date: [Date]
• Preferred Time: [Morning/Afternoon/Evening]
• Special Instructions: [Any specific requirements]

💬 *Please send me:*
1. Exact pricing for my property
2. Available time slots
3. What to prepare before service

📍 *Location:*
Best contact time: [Morning/Afternoon/Evening]`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const serviceTabs = [
    { id: "residential", label: "Residential", icon: <Home size={20} /> },
    { id: "commercial", label: "Commercial", icon: <Building size={20} /> },
    { id: "emergency", label: "Emergency", icon: <Droplets size={20} /> },
    { id: "specialty", label: "Specialty", icon: <Sofa size={20} /> },
  ];

  const servicesData = {
    residential: [
      {
        id: 1,
        title: "Regular House Cleaning",
        description:
          "Weekly, bi-weekly, or monthly cleaning to keep your home spotless.",
        icon: <Sparkles className="text-blue-600" size={28} />,
        features: [
          "Dusting & polishing",
          "Floor cleaning",
          "Kitchen deep clean",
          "Bathroom sanitization",
        ],
        price: "From $120",
        duration: "2-4 hours",
        popular: true,
      },
      {
        id: 2,
        title: "Deep Cleaning Service",
        description:
          "Intensive cleaning for a fresh start or seasonal refresh.",
        icon: <Wind className="text-green-600" size={28} />,
        features: [
          "Inside appliances",
          "Window tracks",
          "Baseboards",
          "Light fixtures",
        ],
        price: "From $250",
        duration: "4-6 hours",
      },
      {
        id: 3,
        title: "Move In/Out Cleaning",
        description: "Complete property cleaning for tenants and landlords.",
        icon: <Car className="text-purple-600" size={28} />,
        features: [
          "Carpet cleaning",
          "Wall washing",
          "Cabinets inside/out",
          "Final inspection",
        ],
        price: "From $300",
        duration: "5-8 hours",
        popular: true,
      },
      {
        id: 4,
        title: "Curtain Cleaning",
        description:
          "Professional cleaning to remove dust, stains, and allergens from curtains and drapes.",
        icon: <Calendar className="text-amber-600" size={28} />,
        features: [
          "Dust & allergen removal",
          "Stain treatment",
          "Fabric-safe deep cleaning",
          "On-site or off-site service",
        ],
        price: "From $120",
        duration: "2-4 hours",
      },
    ],
    commercial: [
      {
        id: 1,
        title: "Office Cleaning",
        description:
          "Daily, weekly, or monthly office cleaning for productivity.",
        icon: <Building className="text-blue-600" size={28} />,
        features: [
          "Desk sanitization",
          "Common areas",
          "Restrooms",
          "Kitchenette",
        ],
        price: "Custom Quote",
        duration: "Scheduled",
        popular: true,
      },
      {
        id: 2,
        title: "Retail Store Cleaning",
        description: "Maintain a clean shopping environment for customers.",
        icon: <Home className="text-green-600" size={28} />,
        features: [
          "Floor maintenance",
          "Display cleaning",
          "Restrooms",
          "Entrance areas",
        ],
        price: "Custom Quote",
        duration: "Nightly/Weekly",
      },
      {
        id: 3,
        title: "Medical Facility Cleaning",
        description: "Specialized cleaning for healthcare environments.",
        icon: <Shield className="text-red-600" size={28} />,
        features: [
          "Disinfection protocols",
          "Biohazard safety",
          "Equipment cleaning",
          "Sterile areas",
        ],
        price: "Custom Quote",
        duration: "24/7 Available",
        popular: true,
      },
      {
        id: 4,
        title: "Industrial Cleaning",
        description: "Heavy-duty cleaning for warehouses and factories.",
        icon: <Wind className="text-orange-600" size={28} />,
        features: [
          "Floor scrubbing",
          "Equipment cleaning",
          "High dusting",
          "Waste removal",
        ],
        price: "Custom Quote",
        duration: "As needed",
      },
    ],
    emergency: [
      {
        id: 1,
        title: "Flood Restoration",
        description: "24/7 emergency water extraction and drying services.",
        icon: <Droplets className="text-blue-600" size={28} />,
        features: [
          "Water extraction",
          "Drying & dehumidifying",
          "Mold prevention",
          "Structural drying",
        ],
        price: "Emergency Call",
        duration: "45 min response",
        popular: true,
      },
      {
        id: 2,
        title: "Fire Damage Cleaning",
        description: "Soot removal and smoke odor elimination.",
        icon: <Sparkles className="text-red-600" size={28} />,
        features: [
          "Soot removal",
          "Odor elimination",
          "Surface cleaning",
          "Air purification",
        ],
        price: "Emergency Call",
        duration: "1 hour response",
      },
      {
        id: 3,
        title: "Biohazard Cleanup",
        description: "Professional cleanup of hazardous materials.",
        icon: <Shield className="text-purple-600" size={28} />,
        features: [
          "Safe disposal",
          "Disinfection",
          "Odor control",
          "Documentation",
        ],
        price: "Emergency Call",
        duration: "2 hour response",
      },
      {
        id: 4,
        title: "Emergency Disinfection",
        description: "Rapid response for virus and bacteria outbreaks.",
        icon: <CheckCircle className="text-green-600" size={28} />,
        features: [
          "Fogging treatment",
          "Surface disinfection",
          "Air purification",
          "Preventive measures",
        ],
        price: "Emergency Call",
        duration: "30 min response",
        popular: true,
      },
    ],
    specialty: [
      {
        id: 1,
        title: "Carpet & Rug Cleaning",
        description: "Deep steam cleaning to remove stains and allergens.",
        icon: <Sofa className="text-blue-600" size={28} />,
        features: [
          "Steam cleaning",
          "Stain removal",
          "Deodorizing",
          "Protective treatment",
        ],
        price: "From $80/room",
        duration: "2-3 hours",
        popular: true,
      },
      {
        id: 2,
        title: "Upholstery Cleaning",
        description: "Professional cleaning for sofas, chairs, and curtains.",
        icon: <Home className="text-purple-600" size={28} />,
        features: [
          "Fabric-safe cleaning",
          "Stain removal",
          "Odor elimination",
          "Protective coating",
        ],
        price: "From $150",
        duration: "1-2 hours",
      },
      {
        id: 3,
        title: "Window Cleaning",
        description: "Interior and exterior window cleaning services.",
        icon: <Wind className="text-teal-600" size={28} />,
        features: [
          "Streak-free finish",
          "Track cleaning",
          "Screen cleaning",
          "Glass polishing",
        ],
        price: "From $120",
        duration: "2-4 hours",
      },
      {
        id: 4,
        title: "Air Duct Cleaning",
        description: "Improve indoor air quality with duct cleaning.",
        icon: <Calendar className="text-amber-600" size={28} />,
        features: [
          "Duct vacuuming",
          "Sanitization",
          "Filter replacement",
          "Air quality test",
        ],
        price: "From $300",
        duration: "3-5 hours",
        popular: true,
      },
      {
        id: 5,
        title: "Rug Cleaning",
        description: "Specialized cleaning for area rugs and oriental carpets.",
        icon: <Square className="text-rose-600" size={28} />,
        features: [
          "Hand washing",
          "Stain treatment",
          "Gentle drying",
          "Fringe cleaning",
        ],
        price: "From $100/rug",
        duration: "3-4 hours",
        popular: true,
      },
      {
        id: 6,
        title: "Mattress Cleaning",
        description:
          "Deep cleaning to remove allergens, dust mites, and stains.",
        icon: <Bed className="text-indigo-600" size={28} />,
        features: [
          "Allergen removal",
          "Stain treatment",
          "Sanitization",
          "Deodorizing",
        ],
        price: "From $90/mattress",
        duration: "1-2 hours",
      },
      {
        id: 7,
        title: "Rugs Deep Cleaning",
        description: "Professional deep cleaning for all types of rugs.",
        icon: <Square className="text-emerald-600" size={28} />,
        features: [
          "Deep extraction",
          "pH-balanced solutions",
          "Mold prevention",
          "Texture restoration",
        ],
        price: "From $120",
        duration: "2-3 hours",
      },
      // NEW: Car Seats Cleaning Service
      {
        id: 8,
        title: "Car Seats Cleaning",
        description: "Professional deep cleaning for fabric and leather car seats. Remove stains, odors, and allergens.",
        icon: <Car className="text-slate-600" size={28} />,
        features: [
          "Fabric & leather cleaning",
          "Stain & odor removal",
          "Allergen elimination",
          "Sanitization treatment",
          "Pet hair & dander removal",
          "Upholstery protection",
        ],
        price: "From $99",
        duration: "1-2 hours",
        popular: false,
      },
    ],
  };

  const serviceStats = [
    {
      value: "98%",
      label: "Customer Satisfaction",
      icon: <ThumbsUp className="text-green-500" size={20} />,
    },
    {
      value: "15+",
      label: "Years Experience",
      icon: <Award className="text-blue-500" size={20} />,
    },
    {
      value: "45 min",
      label: "Average Response Time",
      icon: <Clock className="text-amber-500" size={20} />,
    },
    {
      value: "500+",
      label: "5-Star Reviews",
      icon: <Star className="text-purple-500" size={20} />,
    },
  ];

  const processSteps = [
    {
      step: "01",
      title: "Free Consultation",
      description: "We assess your needs and provide a transparent quote.",
      icon: <Users className="text-blue-600" size={24} />,
    },
    {
      step: "02",
      title: "Schedule Service",
      description: "Choose a convenient time that works for you.",
      icon: <Calendar className="text-green-600" size={24} />,
    },
    {
      step: "03",
      title: "Professional Cleaning",
      description: "Our trained team delivers exceptional results.",
      icon: <Sparkles className="text-purple-600" size={24} />,
    },
    {
      step: "04",
      title: "Quality Check",
      description: "We ensure 100% satisfaction with every service.",
      icon: <CheckCircle className="text-amber-600" size={24} />,
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Sparkles size={16} />
            PREMIUM SERVICES
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Professional Cleaning{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
              Solutions
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
            From regular home maintenance to emergency restoration, we provide
            comprehensive cleaning services tailored to your needs. Experience
            the Knight Cleanings difference.
          </p>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {serviceStats.map((stat, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="flex items-center justify-center gap-3 mb-2">
                  {stat.icon}
                  <div className="text-3xl font-bold text-gray-900">
                    {stat.value}
                  </div>
                </div>
                <div className="text-sm text-gray-600 text-center font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Service Tabs */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {serviceTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md"
                }`}
              >
                {tab.icon}
                <span className="font-semibold">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {servicesData[activeTab].map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-gray-100"
              >
                {/* Card Header */}
                <div className="relative p-6">
                  {service.popular && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                        POPULAR
                      </span>
                    </div>
                  )}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl inline-flex mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {service.description}
                  </p>
                </div>

                {/* Features List */}
                <div className="px-6 pb-6">
                  <div className="space-y-2 mb-6">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="text-green-500" size={16} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price & Duration */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {service.price}
                      </div>
                      <div className="text-sm text-gray-500">
                        Starting price
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        {service.duration}
                      </div>
                      <div className="text-sm text-gray-500">Duration</div>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col gap-3">
                    {/* WhatsApp Booking Button */}
                    <button
                      onClick={() => handleWhatsAppBooking(service)}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-lg hover:from-green-600 hover:to-emerald-700 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={18} />
                      Book via WhatsApp
                    </button>
                    
                    {/* Alternative Booking Link */}
                    <Link
                      to="/booking"
                      className="block w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold py-3 px-4 rounded-lg text-center hover:from-blue-700 hover:to-indigo-800 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      Book Online
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Steps */}
        <div className="mt-20 mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Our Simple Process
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Getting started with Knight Cleanings is easy. Follow these simple
              steps to enjoy a spotless space.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connecting Line for Desktop */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-3/4 w-full h-1 bg-gradient-to-r from-blue-200 to-indigo-200 z-0"></div>
                )}

                <div className="relative bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 z-10">
                  <div className="absolute -top-4 left-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold text-lg w-12 h-12 rounded-full flex items-center justify-center">
                    {step.step}
                  </div>
                  <div className="mt-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-xl inline-flex">
                      {step.icon}
                    </div>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h4>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-12 text-white overflow-hidden relative">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage:
                'url("https://images.unsplash.com/photo-1583947215259-38e31be8751f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
            }}
          ></div>

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">
                Ready for a Cleaner Space?
              </h3>
              <p className="text-blue-100 text-lg mb-6">
                Experience the difference with our professional cleaning
                services. Get a free, no-obligation quote today and see why
                thousands trust Knight Cleanings.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Shield className="text-green-300" size={20} />
                  <span className="text-blue-100">
                    100% Satisfaction Guarantee
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="text-green-300" size={20} />
                  <span className="text-blue-100">
                    Same Day Service Available
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* WhatsApp Quote Button */}
              <button
                onClick={() => handleWhatsAppBooking({
                  title: "General Quote Request",
                  description: "Looking for a free quote for cleaning services",
                  features: ["Free consultation", "Transparent pricing", "Same day service"],
                  price: "Custom Quote",
                  duration: "Flexible",
                  popular: true
                })}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-center flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                Get WhatsApp Quote
              </button>
              
              <Link
                to="/services"
                className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all duration-300 text-center"
              >
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;